import { useMemo } from 'react';

import {
  EligibilityParameters,
  PersonalizedSearchForm,
  PostcodeInfo,
} from '@keaze/web/common/interfaces';
import { LocalStorageKeys } from '@keaze/web/common/enums';
import { useLocalStorage } from '@keaze/web/hooks';
import { DEFAULT_PERSONALIZED_SEARCH_DATA } from '@keaze/web/utils/defaultValues';

type UsePersonalizedSearchExpected = {
  personalizedSearchData: EligibilityParameters;
  isPersonalizedSearchData: boolean;
};

type UsePersonalizedSearch = () => UsePersonalizedSearchExpected;

type GeneratePersonalizedSearchDataArgs = {
  storedPersonalizedSearchData: PersonalizedSearchForm | null;
  storedHomeLocation: PostcodeInfo | null;
  storedWorkLocation: PostcodeInfo | null;
};

type GeneratePersonalizedSearchData = (
  args: GeneratePersonalizedSearchDataArgs
) => EligibilityParameters;

const generatePersonalizedSearchData: GeneratePersonalizedSearchData = ({
  storedPersonalizedSearchData,
  storedHomeLocation,
  storedWorkLocation,
}) => {
  const { householdIncome, estimatedDeposit, address, workAddress } =
    storedPersonalizedSearchData ?? DEFAULT_PERSONALIZED_SEARCH_DATA;

  return {
    home_postcode: address,
    work_postcode: workAddress,
    household_income: Number(householdIncome),
    deposit: Number(estimatedDeposit),
    home_local_authority:
      storedHomeLocation === null ? 0 : storedHomeLocation.id,
    work_local_authority:
      storedWorkLocation === null ? 0 : storedWorkLocation.id,
  };
};

export const usePersonalizedSearch: UsePersonalizedSearch = () => {
  const [storedHomeLocation] = useLocalStorage<PostcodeInfo | null>(
    LocalStorageKeys.addressResponse,
    null
  );
  const [storedWorkLocation] = useLocalStorage<PostcodeInfo | null>(
    LocalStorageKeys.workAddressResponse,
    null
  );
  const [
    storedPersonalizedSearchData,
  ] = useLocalStorage<PersonalizedSearchForm | null>(
    LocalStorageKeys.personalizedSearch,
    null
  );

  const isPersonalizedSearchData = useMemo(
    () => !!storedPersonalizedSearchData,
    [storedPersonalizedSearchData]
  );

  const personalizedSearchData = useMemo(
    () =>
      generatePersonalizedSearchData({
        storedPersonalizedSearchData,
        storedHomeLocation,
        storedWorkLocation,
      }),
    [storedPersonalizedSearchData, storedHomeLocation, storedWorkLocation]
  );

  return {
    personalizedSearchData,
    isPersonalizedSearchData,
  };
};
